import { ArrowLeft, Pencil, Trash2 } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useCan } from '#client/hooks/useCan';
import { useResourceItem } from '#client/hooks/useResourceItem';
import type { Permission } from '#shared/types';
import { Button } from './Button';
import { ConfirmDeleteModal } from './ConfirmDeleteModal';
import { Page } from './Page';
import { RecordPage } from './RecordPage';

interface ResourceDetailPageProps<T extends { id: number }> {
  /** API path and route base, e.g. `/lab-tests`. They are the same by convention. */
  path: string;
  /**
   * Singular lowercase noun, e.g. "lab test".
   *
   * Drives the loading heading ("Lab test"), the not-found banner ("Lab test
   * not found") and the delete prompt ("Delete lab test"), so no detail screen
   * writes them out by hand.
   */
  noun: string;
  title: (record: T) => ReactNode;
  subtitle?: ((record: T) => ReactNode) | undefined;
  /** Name shown in the delete confirmation, e.g. `${name} (${pin_no})`. */
  deleteLabel: (record: T) => string;
  /** Omit to show the action to anyone who can reach the screen. */
  editCapability?: Permission | undefined;
  deleteCapability?: Permission | undefined;
  /**
   * A per-record rule, ANDed with `deleteCapability`.
   *
   * Some rules are about the record rather than the role: an admin may delete
   * users, but not their own account, because doing so invalidates the session
   * they are using.
   */
  canDelete?: ((record: T) => boolean) | undefined;
  /**
   * The same per-record rule for Edit, ANDed with `editCapability`.
   *
   * Some records stop being editable without anybody's role changing: a
   * reagent order that has been approved cannot have its lines rewritten,
   * because that would change the thing somebody approved. Offering the button
   * anyway means a form that 409s on save, which reads as a bug rather than as
   * the rule it is.
   */
  canEdit?: ((record: T) => boolean) | undefined;
  /** Extra secondary actions, rendered after Delete to match the existing order. */
  extraActions?: ((record: T) => ReactNode) | undefined;
  fullWidth?: boolean | undefined;
  /** `refresh` re-fetches the record — for an action on the page that changes it. */
  children: (record: T, refresh: () => void) => ReactNode;
}

/**
 * The detail screen shared by every resource: fetch, load and not-found states,
 * a back button, Edit and Delete, and the delete confirmation.
 *
 * It owns the fetch, mirroring `ResourceListPage`, which already owns its list.
 * A screen that needs something else — two parallel fetches, or a record it
 * already holds — composes `useApiResource` and `RecordPage` directly, as the
 * report entry and print screens do. There is deliberately no `record`
 * override prop here; that would be a second, untested path through the same
 * component.
 */
export function ResourceDetailPage<T extends { id: number }>({
  path,
  noun,
  title,
  subtitle,
  deleteLabel,
  editCapability,
  canEdit,
  deleteCapability,
  canDelete,
  extraActions,
  fullWidth = false,
  children,
}: ResourceDetailPageProps<T>) {
  const { id } = useParams();
  const navigate = useNavigate();
  const can = useCan();
  const { data, loading, error, refresh } = useResourceItem<T>(path, id);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

  const heading = noun.charAt(0).toUpperCase() + noun.slice(1);
  const back = (
    <Button icon={<ArrowLeft />} aria-label="Back" onClick={() => void navigate(path)} />
  );

  const roleCanEdit = editCapability === undefined || can(editCapability);
  const roleCanDelete = deleteCapability === undefined || can(deleteCapability);

  return (
    <RecordPage
      title={heading}
      backAction={back}
      record={data}
      loading={loading}
      error={error}
      missingMessage={`${heading} not found`}
      fullWidth={fullWidth}
    >
      {(record) => {
        const showDelete = roleCanDelete && (canDelete?.(record) ?? true);
        const showEdit = roleCanEdit && (canEdit?.(record) ?? true);

        return (
          <Page
            title={title(record)}
            subtitle={subtitle?.(record)}
            backAction={back}
            fullWidth={fullWidth}
            secondaryActions={
              <>
                {showDelete && <DeleteButton onClick={() => setIsConfirmingDelete(true)} />}
                {extraActions?.(record)}
              </>
            }
            primaryAction={
              showEdit ? (
                <EditButton onClick={() => void navigate(`${path}/${record.id}/edit`)} />
              ) : undefined
            }
          >
            {children(record, refresh)}

            <ConfirmDeleteModal
              target={isConfirmingDelete ? { id: record.id, label: deleteLabel(record) } : null}
              path={path}
              noun={noun}
              onClose={() => setIsConfirmingDelete(false)}
              onDeleted={() => void navigate(path)}
            />
          </Page>
        );
      }}
    </RecordPage>
  );
}

function DeleteButton({ onClick }: { onClick: () => void }) {
  return (
    <Button variant="danger" icon={<Trash2 />} onClick={onClick}>
      Delete
    </Button>
  );
}

function EditButton({ onClick }: { onClick: () => void }) {
  return (
    <Button variant="primary" icon={<Pencil />} onClick={onClick}>
      Edit
    </Button>
  );
}
